import React, { useEffect, useRef, useState } from "react";
import { Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosRequest } from "../../../_config/apiClient";

const MockExamInterface = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  /* ======================= IMPORTANT FIX ======================= */
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const saved = sessionStorage.getItem("currentQuestionIndex");
    return saved ? Number(saved) : 0;
  });

  const [answers, setAnswers] = useState(() => {
    const saved = sessionStorage.getItem("answers");
    return saved ? JSON.parse(saved) : {};
  });
  /* ============================================================= */

  const [questions, setQuestions] = useState([]);
  const [quizInfo, setQuizInfo] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);

  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  const currentQuestion = questions[currentQuestionIndex];

  /* ======================= PERSIST STATE ======================= */
  useEffect(() => {
    sessionStorage.setItem("currentQuestionIndex", currentQuestionIndex);
  }, [currentQuestionIndex]);

  useEffect(() => {
    sessionStorage.setItem("answers", JSON.stringify(answers));
  }, [answers]);
  /* ============================================================= */

  /* ======================= FETCH QUIZ ======================= */
  const fetchStartQuizAttempt = async () => {
    const existing = sessionStorage.getItem("quizAttemptId");
    if (existing) return;

    const res = await axiosRequest(
      "post",
      "/user/startQuizAttempt",
      { quizId: id },
      { Authorization: `Bearer ${localStorage.getItem("user")}` }
    );

    sessionStorage.setItem("quizAttemptId", res.data._id);
    sessionStorage.setItem("quizStartedAt", res.data.startedAt);
    sessionStorage.setItem("isPaused", "false");
  };

  const fetchQuizQuestionList = async () => {
    const res = await axiosRequest(
      "post",
      "/user/quizQuestionList",
      { quizId: id },
      { Authorization: `Bearer ${localStorage.getItem("user")}` }
    );

    const { quizInfo, questions } = res.data;

    const formatted = questions
      .sort((a, b) => a.questionNo - b.questionNo)
      .map((q) => ({
        id: q._id,
        questionNo: q.questionNo,
        text: q.questionText,
        options: Object.entries(q.options).map(([label, text]) => ({
          label,
          text,
        })),
      }));

    setQuizInfo(quizInfo);
    setQuestions(formatted);
    setLoading(false);
  };

  useEffect(() => {
    if (!id) return navigate("/");
    fetchQuizQuestionList();
    fetchStartQuizAttempt();
  }, [id, navigate]);

  /* ======================= TIMER ======================= */
  useEffect(() => {
    if (!quizInfo?.durationMinutes) return;

    const total = quizInfo.durationMinutes * 60;
    const stored = Number(sessionStorage.getItem("secondsLeft"));
    const lastTick = Number(sessionStorage.getItem("lastTickTime"));
    const paused = sessionStorage.getItem("isPaused") === "true";

    let seconds = stored || total;

    if (!paused && lastTick) {
      const diff = Math.floor((Date.now() - lastTick) / 1000);
      seconds = Math.max(seconds - diff, 0);
    }

    setSecondsLeft(seconds);
    setIsPaused(paused);

    sessionStorage.setItem("secondsLeft", seconds);
    sessionStorage.setItem("lastTickTime", Date.now());
  }, [quizInfo]);

  useEffect(() => {
    if (isPaused) return;

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          submitTest();
          return 0;
        }
        sessionStorage.setItem("secondsLeft", prev - 1);
        sessionStorage.setItem("lastTickTime", Date.now());
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [isPaused]);

  /* ======================= RESTORE OPTION ======================= */
  useEffect(() => {
    setSelectedOption(answers[currentQuestion?.id] || null);
  }, [currentQuestionIndex, currentQuestion, answers]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  /* ======================= ACTIONS ======================= */
  const handlePauseResume = async () => {
    const attemptId = sessionStorage.getItem("quizAttemptId");

    await axiosRequest(
      "post",
      "/user/togglePauseQuizAttempt",
      { attemptId },
      { Authorization: `Bearer ${localStorage.getItem("user")}` }
    );

    setIsPaused((p) => {
      sessionStorage.setItem("isPaused", !p);
      sessionStorage.setItem("lastTickTime", Date.now());
      return !p;
    });
  };

  const handleOptionSelect = (option) => {
    if (isPaused) return;
    setSelectedOption(option);
  };

  const clearAnswer = () => {
    if (isPaused) return;
    setSelectedOption(null);
    setAnswers((prev) => {
      const copy = { ...prev };
      delete copy[currentQuestion.id];
      return copy;
    });
  };

  const handleSubmitAnswer = async () => {
    if (isPaused || !selectedOption) return;

    const attemptId = sessionStorage.getItem("quizAttemptId");

    await axiosRequest(
      "post",
      "/user/saveQuizAnswer",
      {
        attemptId,
        questionId: currentQuestion.id,
        selectedOption,
      },
      { Authorization: `Bearer ${localStorage.getItem("user")}` }
    );

    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: selectedOption,
    }));

    // ✅ LAST QUESTION CHECK
    if (currentQuestionIndex === questions.length - 1) {
      await submitTest();
    } else {
      setCurrentQuestionIndex((i) => i + 1);
    }

  };

  const goPrev = () =>
    !isPaused && currentQuestionIndex > 0 &&
    setCurrentQuestionIndex((i) => i - 1);

  const goNext = () =>
    !isPaused && currentQuestionIndex < questions.length - 1 &&
    setCurrentQuestionIndex((i) => i + 1);

  const submitTest = async () => {
    const attemptId = sessionStorage.getItem("quizAttemptId");

    await axiosRequest(
      "post",
      "/user/submitQuizAttempt",
      { attemptId },
      { Authorization: `Bearer ${localStorage.getItem("user")}` }
    );

    sessionStorage.clear();
    navigate(`/quiz-result/${attemptId}`);
  };

  if (loading) return <div className="p-10 text-center">Loading exam...</div>;


  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* HEADER */}
      <header className="h-16 bg-white shadow flex items-center px-6 justify-between sticky top-0 z-30">
        <h1 className="text-xl font-bold text-blue-700">{quizInfo?.title}</h1>

        <div className="flex items-center gap-4">
          <div className="flex items-center font-semibold">
            <Clock className="h-5 w-5 mr-1" />
            {formatTime(secondsLeft)}
          </div>
          <button
            onClick={handlePauseResume}
            className={`px-3 py-1.5 text-white rounded ${isPaused ? "bg-green-600" : "bg-yellow-500"
              }`}
          >
            {isPaused ? "Resume" : "Pause"}
          </button>
        </div>
      </header>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT */}
        <div className="w-full lg:w-3/5 bg-white overflow-y-auto p-6">
          <div className="mb-4 text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </div>

          <h2 className="text-xl font-semibold mb-6">
            Q{currentQuestion.questionNo}. {currentQuestion.text}
          </h2>

          <div className="space-y-4">
            {currentQuestion.options.map((option) => (
              <label
                key={option.label}
                onClick={() => handleOptionSelect(option.label)}
                className={`flex cursor-pointer p-4 rounded-lg border transition ${selectedOption === option.label
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:bg-gray-50"
                  }`}
              >
                <input
                  type="radio"
                  readOnly
                  checked={selectedOption === option.label}
                  className="mt-1"
                />
                <span className="ml-3">
                  <strong>{option.label}</strong>. {option.text}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="hidden lg:flex lg:w-2/5 flex-col gap-6 p-6 overflow-y-auto">
          <div className="bg-white rounded shadow p-4">
            <h3 className="font-semibold mb-4">Question Palette</h3>
            <div className="grid grid-cols-7 gap-2">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => !isPaused && setCurrentQuestionIndex(index)}
                  className={`h-10 rounded font-medium border ${index === currentQuestionIndex
                    ? "bg-blue-100 border-blue-600"
                    : answers[q.id]
                      ? "bg-green-100 border-green-500"
                      : "border-gray-300"
                    }`}
                >
                  {q.questionNo}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded shadow p-4 space-y-3 sticky top-20">
            <div className="flex gap-2">
              <button onClick={goPrev} className="flex-1 bg-gray-200 py-2 rounded">
                <ArrowLeft className="inline mr-1 h-4 w-4" /> Prev
              </button>

              <button
                onClick={clearAnswer}
                className="flex-1 bg-orange-500 text-white py-2 rounded"
              >
                Clear
              </button>

              <button onClick={goNext} className="flex-1 bg-blue-600 text-white py-2 rounded">
                Next <ArrowRight className="inline ml-1 h-4 w-4" />
              </button>

              <button
                onClick={handleSubmitAnswer}
                disabled={!selectedOption || isPaused}
                className={`flex-1 py-2 rounded ${!selectedOption || isPaused
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-orange-500 text-white"
                  }`}
              >
                {currentQuestionIndex === questions.length - 1
                  ? "Submit & Finish"
                  : "Submit Answer"}
              </button>
            </div>

            <button
              onClick={submitTest}
              className="w-full bg-green-600 text-white py-2 rounded"
            >
              Submit Test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockExamInterface;
